import { Pagination } from "./pagination.types";

export enum SignalType {
  Welcome = "WELCOME",
  WelcomeBack = "WELCOME_BACK",
  EmailOpened = "threadPixelAccess",
  // ThreadUpdated = "threadOnTrackUpdated",
  ToAdded = "NEW_TO_SAME_AS_BUYER_SENDER_DOMAIN",
  ToRemoved = "TO_REMOVED_SAME_AS_BUYER_SENDER_DOMAIN",
  CcAdded = "NEW_CC_SAME_AS_BUYER_SENDER_DOMAIN",
  CcRemoved = "CC_REMOVED_SAME_AS_BUYER_SENDER_DOMAIN",
  ToThirdPartyAdded = "NEW_TO_NOT_BUYER_SENDER_DOMAIN_NOT_USER_DOMAIN",
  ToThirdPartyRemoved = "TO_REMOVED_NOT_BUYER_SENDER_DOMAIN_NOT_USER_DOMAIN",
  CcThirdPartyAdded = "NEW_CC_NOT_BUYER_SENDER_DOMAIN_NOT_USER_DOMAIN",
  CcThirdPartyRemoved = "CC_REMOVED_NOT_BUYER_SENDER_DOMAIN_NOT_USER_DOMAIN",
  PointOfContactChanged = "MOST_ACTIVE_PARTICIPANT_CHANGE_ON_BUYER_SIDE",
  NewTeamMember = "newUser",
  SentimentIncreased = "BUYER_SENTIMENT_CHANGED_TO_POSITIVE_AFTER_2_NON_POSITIVE_AND_STILL_POSITIVE",
  SentimentDecreased = "BUYER_SENTIMENT_CHANGED_TO_NEGATIVE_AFTER_2_POSITIVE_AND_STILL_NEGATIVE",
}

export enum SignalParticipantType {
  Sender = "sender",
  To = "to",
  Cc = "cc",
  Bcc = "bcc",
}

export enum SignalDeviceType {
  Desktop = "desktop",
  Mobile = "mobile",
}

export interface ExtendedSignalParticipant {
  email: string;
  name: string;
  avatar: string | null;
  type: SignalParticipantType;
  isUser: boolean;
  isUserDomain: boolean;
  isDominant: boolean;
}

export interface SignalParticipant
  extends Pick<ExtendedSignalParticipant, "email" | "name" | "avatar" | "type"> {}

export interface SignalSenderParticipant extends Omit<SignalParticipant, "type"> {}

export interface SignalBuyerParticipant
  extends Omit<ExtendedSignalParticipant, "type" | "isDominant"> {}

export interface SignalNewTeamMember extends Omit<SignalParticipant, "type"> {}

export interface SignalEmailOpenedParticipant {
  email: string;
  name: string;
  avatarSrc: string | null;
  isUser: boolean;
  isUserSide: boolean;
}

export interface SignalThreadAccessEvent {
  accessDate: string;
  eventMetadata: {
    location: {
      country: string;
      city: string;
    };
    deviceType: SignalDeviceType | null;
    recipientEmails: string[];
  };
}

export interface SignalCarbonLogicParticipants {
  sender: Omit<ExtendedSignalParticipant, "type">;
  to: Omit<ExtendedSignalParticipant, "type">;
}

export interface SignalEmailRecipients {
  to: (SignalParticipant & { type: SignalParticipantType.To })[];
  cc: (SignalParticipant & { type: SignalParticipantType.Cc })[];
  bcc: (SignalParticipant & { type: SignalParticipantType.Bcc })[];
}

export interface SignalSentiment {
  avgOfPreviousTwo: number;
  currentSentiment: number;
}

export interface SignalThreadData {
  theFirstTrackedEmail: {
    sentAt: string;
    messageId?: string;
  };
  platformThreadId: string;
  participants: SignalEmailOpenedParticipant[];
  subject: string;
  latestEngagementUrl: string;
  opened: boolean;
  replied: boolean;
  onTrack: boolean | null;
}

export interface SignalMetadataMap {
  [SignalType.Welcome]: {};
  [SignalType.WelcomeBack]: {};
  [SignalType.EmailOpened]: {
    thread: SignalThreadData;
    accessEvents: Pagination & {
      results: SignalThreadAccessEvent[];
    };
  };
  // [SignalType.ThreadUpdated]: {
  //   thread: SignalThreadData;
  //   accessEvents: Pagination & {
  //     results: SignalThreadAccessEvent[];
  //   };
  // };
  [SignalType.ToAdded]: {
    sender: SignalSenderParticipant;
    added: SignalParticipant[];
    buyer: SignalBuyerParticipant;
    threadUrlId: string;
    emailSubject: string;
    emailSentAt: string;
    sentByUser: boolean;
    platformMessageId: string;
    emailRecipients: SignalEmailRecipients;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    latestEngagementUrl?: string;
  };
  [SignalType.ToRemoved]: {
    sender: SignalSenderParticipant;
    removed: SignalParticipant[];
    buyer: SignalBuyerParticipant;
    threadUrlId: string;
    emailSubject: string;
    emailSentAt: string;
    sentByUser: boolean;
    platformMessageId: string;
    emailRecipients: SignalEmailRecipients;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    latestEngagementUrl?: string;
  };
  [SignalType.CcAdded]: {
    sender: SignalSenderParticipant;
    added: SignalParticipant[];
    emailUrl: string | null;
    emailSubject: string;
    emailSentAt: string;
    sentByUser: boolean;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    threadUrlId: string;
    platformMessageId: string;
    latestEngagementUrl?: string;
  };
  [SignalType.CcRemoved]: {
    removed: SignalParticipant[];
    emailSubject: string;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    threadUrlId: string;
    platformMessageId: string;
    latestEngagementUrl?: string;
  };
  [SignalType.ToThirdPartyAdded]: {
    added: SignalParticipant[];
    emailSubject: string;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    threadUrlId: string;
    platformMessageId: string;
    latestEngagementUrl?: string;
  };
  [SignalType.ToThirdPartyRemoved]: {
    removed: SignalParticipant[];
    emailSubject: string;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    threadUrlId: string;
    platformMessageId: string;
    latestEngagementUrl?: string;
  };
  [SignalType.CcThirdPartyAdded]: {
    added: SignalParticipant[];
    emailSubject: string;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    threadUrlId: string;
    platformMessageId: string;
    latestEngagementUrl?: string;
  };
  [SignalType.CcThirdPartyRemoved]: {
    removed: SignalParticipant[];
    emailSubject: string;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    threadUrlId: string;
    platformMessageId: string;
    latestEngagementUrl?: string;
  };
  [SignalType.PointOfContactChanged]: {
    emailSubject: string;
    threadUrlId: string;
    platformMessageId: string;
    emailSentAt: string;
    sentByUser: boolean;
    previousMostActiveParticipantBuyerSide: SignalBuyerParticipant;
    currentMostActiveParticipantBuyerSide: SignalBuyerParticipant;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    latestEngagementUrl?: string;
  };
  [SignalType.NewTeamMember]: {
    newUser: SignalNewTeamMember;
  };
  [SignalType.SentimentIncreased]: {
    threadUrlId: string;
    emailSubject: string;
    emailSentAt: string;
    sentByUser: boolean;
    platformMessageId: string;
    sender: SignalSenderParticipant;
    sentiment: SignalSentiment;
    emailRecipients: SignalEmailRecipients;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    latestEngagementUrl?: string;
  };
  [SignalType.SentimentDecreased]: {
    threadUrlId: string;
    emailSubject: string;
    emailSentAt: string;
    sentByUser: boolean;
    platformMessageId: string;
    sender: SignalSenderParticipant;
    sentiment: SignalSentiment;
    emailRecipients: SignalEmailRecipients;
    carbonLogicParticipants: SignalCarbonLogicParticipants;
    latestEngagementUrl?: string;
  };
}

export interface GenericSignal<T extends SignalType> {
  eventId: string;
  eventName: T;
  eventMetadata: SignalMetadataMap[T];
  eventDate: string;
  eventCreatedAt?: string;
  userId: string;
  orgId: string;
  read: boolean;
  viewed: boolean;
}

export interface ThreadOnTrackSignal extends SignalThreadData {
  notificationRead: boolean;
  onTrack: boolean;
  accessEvents: Pagination & {
    results: SignalThreadAccessEvent[];
  };
}

export type Signal =
  | GenericSignal<SignalType.Welcome>
  | GenericSignal<SignalType.WelcomeBack>
  | GenericSignal<SignalType.EmailOpened>
  | GenericSignal<SignalType.ToAdded>
  | GenericSignal<SignalType.ToRemoved>
  | GenericSignal<SignalType.CcAdded>
  | GenericSignal<SignalType.CcRemoved>
  | GenericSignal<SignalType.ToThirdPartyAdded>
  | GenericSignal<SignalType.ToThirdPartyRemoved>
  | GenericSignal<SignalType.CcThirdPartyAdded>
  | GenericSignal<SignalType.CcThirdPartyRemoved>
  | GenericSignal<SignalType.PointOfContactChanged>
  | GenericSignal<SignalType.NewTeamMember>
  | GenericSignal<SignalType.SentimentIncreased>
  | GenericSignal<SignalType.SentimentDecreased>;
// | GenericSignal<SignalType.ThreadUpdated>;
